import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkle, Warning, Stack, X, Percent } from "@phosphor-icons/react";
import { CategorySelect } from "@/components/CategorySelect";

export function AiBudgetDialog({
  aiOpen, setAiOpen, aiRows, setAiRows, aiFile, setAiFile, aiExtracting, runExtract,
  flagInfo, inLibrary, libHint, saveRowToLibrary, setAiRow, removeAiRow,
  addAiRows, aiAdding, saveToLib, setSaveToLib, aiTotal, money, categories = [],
}) {
  const [markup, setMarkup] = useState("");
  const applyMarkup = () => {
    const pct = Number(markup);
    if (!pct || !aiRows) return;
    const f = 1 + pct / 100;
    setAiRows(aiRows.map((r) => ({ ...r, budget: Math.round((Number(r.budget) || 0) * f * 100) / 100 })));
    setMarkup("");
  };
  return (
    <Dialog open={aiOpen} onOpenChange={setAiOpen}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkle size={20} weight="fill" /> AI Budget from Document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="ai-budget-dialog">
          {aiRows === null ? (
            <>
              <p className="text-sm text-muted-foreground">
                Upload a contractor estimate, quote, invoice, or budget spreadsheet
                (<strong>PDF, image/scan, or CSV/Excel</strong>). AI reads it, auto-suggests the phase for
                each cost, and drafts budget line items you can review before adding.
              </p>
              <div>
                <Label className="label-caps">Document</Label>
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.xlsx,.xls" onChange={(e) => setAiFile(e.target.files?.[0] || null)}
                  data-testid="ai-file-input" className="mt-2 rounded-none" />
              </div>
              <DialogFooter>
                <Button onClick={runExtract} disabled={aiExtracting || !aiFile} data-testid="ai-extract-button" className="rounded-none">
                  <Sparkle size={16} weight="fill" className="mr-1" />
                  {aiExtracting ? "Reading document…" : "Extract budget"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{aiRows.length} items · review & edit before adding</p>
                <div className="text-sm"><span className="label-caps mr-2">Total</span><span className="font-mono-fig font-semibold">{money(aiTotal)}</span></div>
              </div>
              <div className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid="ai-markup-control">
                <span className="label-caps flex items-center gap-1"><Percent size={13} /> Adjust all budgets by</span>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.1" value={markup} onChange={(e) => setMarkup(e.target.value)}
                    placeholder="e.g. 10" data-testid="ai-markup-input" className="w-24 h-8 rounded-none font-mono-fig" />
                  <span className="text-sm">%</span>
                </div>
                <Button size="sm" variant="outline" onClick={applyMarkup} disabled={!markup} data-testid="ai-markup-apply" className="rounded-none h-8">Apply</Button>
                <span className="text-xs text-muted-foreground">markup / contingency — use a negative value for a discount</span>
              </div>
              {aiRows.some((r) => flagInfo(r)) && (
                <div className="flex items-center gap-2 border border-amber-500 bg-amber-50 text-amber-800 px-3 py-2 text-xs" data-testid="overcharge-legend">
                  <Warning size={14} weight="fill" /> Amber rows are priced notably higher than your past average for that category — double-check for overcharges.
                </div>
              )}
              {aiRows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6">No line items detected. Try a clearer document.</p>
              ) : (
                <div className="border border-border divide-y divide-border max-h-[45vh] overflow-y-auto">
                  <div className="grid grid-cols-12 gap-2 px-2 py-2 bg-secondary text-xs font-semibold sticky top-0">
                    <div className="col-span-2">Phase</div>
                    <div className="col-span-2">Category</div>
                    <div className="col-span-4">Description</div>
                    <div className="col-span-2">Vendor</div>
                    <div className="col-span-1 text-right">Amount</div>
                    <div className="col-span-1"></div>
                  </div>
                  {aiRows.map((r, idx) => {
                    const flag = flagInfo(r);
                    return (
                      <div key={idx} className={`grid grid-cols-12 gap-2 px-2 py-2 items-center ${flag ? "bg-amber-50" : ""}`} data-testid={`ai-row-${idx}`}>
                        <Input className="col-span-2 rounded-none h-8 text-xs" value={r.phase} onChange={(e) => setAiRow(idx, "phase", e.target.value)} />
                        <div className="col-span-2">
                          <CategorySelect categories={categories} value={r.category}
                            onChange={(v) => setAiRow(idx, "category", v)}
                            testId={`ai-row-${idx}-category`}
                            className="rounded-none h-8 text-xs" placeholder="Category" />
                        </div>
                        <Input className="col-span-4 rounded-none h-8 text-xs" value={r.description} onChange={(e) => setAiRow(idx, "description", e.target.value)} />
                        <Input className="col-span-2 rounded-none h-8 text-xs" value={r.vendor} onChange={(e) => setAiRow(idx, "vendor", e.target.value)} />
                        <Input type="number" className={`col-span-1 rounded-none h-8 text-xs font-mono-fig text-right ${flag ? "border-amber-500 text-amber-800" : ""}`} value={r.budget} onChange={(e) => setAiRow(idx, "budget", e.target.value)} />
                        <div className="col-span-1 flex items-center justify-center gap-1">
                          {flag && (
                            <span title={`${flag.pct}% above your avg for this category (avg ${money(flag.avg)})`} data-testid={`ai-flag-${idx}`} className="text-amber-600">
                              <Warning size={14} weight="fill" />
                            </span>
                          )}
                          {inLibrary(r) ? (
                            <span title="Already in your Cost Library" data-testid={`ai-in-lib-${idx}`} className="text-emerald-600"><Stack size={14} weight="fill" /></span>
                          ) : (
                            <button onClick={() => saveRowToLibrary(r)} data-testid={`ai-save-lib-${idx}`}
                              title={`Save to Cost Library${libHint(r) ? ` · ${libHint(r)}` : ""}`}
                              className={libHint(r) ? "text-primary" : "text-muted-foreground hover:text-primary"}>
                              <Stack size={14} weight={libHint(r) ? "bold" : "regular"} />
                            </button>
                          )}
                          <button onClick={() => removeAiRow(idx)} className="text-muted-foreground hover:text-destructive"><X size={14} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <DialogFooter className="gap-2 sm:justify-between items-center">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none mr-auto" data-testid="ai-save-library-label">
                  <input type="checkbox" checked={saveToLib} onChange={(e) => setSaveToLib(e.target.checked)} className="h-4 w-4 accent-black" data-testid="ai-save-library-toggle" />
                  <Stack size={14} /> Also save new items to Cost Library
                </label>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setAiRows(null)} className="rounded-none" data-testid="ai-back-button">Back</Button>
                  <Button onClick={addAiRows} disabled={aiAdding || aiRows.length === 0} data-testid="ai-add-button" className="rounded-none">
                    {aiAdding ? "Adding…" : `Add ${aiRows.length} to budget`}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
